import ModalCustom from "components/ModalCustom"
import ActionFooterModalCard from "components/ModalCustom/ActionFooterModalCard"
import { useEffect, useState, useMemo } from "react"
import FormInputCard from "pages/auth/molecules/FormInputCard"
import SelectMulti from "components/Select/SelectMulti"
import NoDataCard from "pages/project-build/molecules/NoDataCard"
import {
  getUserAddGroupsOrganizationMiddleware,
  postAssignMemberToGroupsOrganizationMiddleware,
} from "../groups.api"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import {
  addRoleGroupOrganizationMiddleware,
  handleUserShareKeyProjectMiddleware,
} from "pages/projects/services/api"
import { closeLoading, startLoading } from "reducers/loading"
import { GroupsOrganizationRoleResponse } from "../groups.type"
import { ReactComponent as IconCloseBlack } from "assets/images/icons/icon-close-black.svg"
import { cloneDeep } from "lodash"
import SelectTippy from "components/Select/SelectTippy"

interface Props {
  isOpen: boolean
  onClose: () => void
  groupId: string
  callback: () => void
  roles: GroupsOrganizationRoleResponse[]
}

export const AddMemberToGroup = (props: Props) => {
  const { onClose, isOpen, groupId, callback, roles } = props
  const roleGroupMemberId = roles.find(
    (role) => role.permissions.length === 0
  )?.id
  const [selectedMembers, setSelectedMembers] = useState<
    Record<
      string,
      {
        label: string
        value: string
        role: string
      }
    >
  >({})
  const [users, setUsers] = useState<
    {
      id: string
      fullname: string
      email: string
    }[]
  >([])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setSelectedMembers({})
    getUserAddGroup()
  }, [isOpen])

  const getUserAddGroup = async () => {
    const response = await getUserAddGroupsOrganizationMiddleware(groupId)
    setUsers(response)
  }

  const handleRemoveUser = (userId: string) => {
    const newSelect = cloneDeep(selectedMembers)
    delete newSelect[userId]
    setSelectedMembers(newSelect)
  }

  const onSubmit = () => {
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: (
              <p>
                Once added, this member will gain access to every project
                associated with this group, including view and edit permissions
                where applicable. Make sure this is intended before confirming
              </p>
            ),
            label: `Warning`,
            content: `This action cannot be undone.`,
            onSubmit: async () => {
              try {
                configureStore.dispatch(startLoading())
                await postAssignMemberToGroupsOrganizationMiddleware({
                  users: JSON.stringify(
                    Object.values(selectedMembers).map((user) => {
                      return {
                        user_id: user.value,
                        role_id: user.role,
                      }
                    })
                  ),
                  group_organization_id: groupId,
                })
                await handleUserShareKeyProjectMiddleware(
                  false,
                  true,
                  undefined,
                  groupId,
                  JSON.stringify(Object.keys(selectedMembers).map((key) => key))
                )
                await addRoleGroupOrganizationMiddleware({
                  is_share_all_project: false,
                  group_ids: JSON.stringify([groupId]),
                  user_ids: JSON.stringify(
                    Object.keys(selectedMembers).map((key) => key)
                  ),
                })
                callback()
                closeModal()
                onClose()
                closeLoading()
                toast(
                  <LabelNotificationPage
                    messenger="Assign member to group successfully!"
                    type="success"
                  />
                )
              } catch (error: any) {
                closeModal()
                closeLoading()
                toast(
                  <LabelNotificationPage
                    messenger={
                      error?.response?.data?.message ||
                      "Assign member to group failed!"
                    }
                    type="error"
                  />
                )
              }
            },
            styleTitle: { textAlign: "center" },
            titleButton: "Yes, Assign to group",
            colorYellowButton: true,
          },
        },
      })
    )
  }

  return (
    <ModalCustom
      label="Add member"
      widthModal={500}
      handleChangeButton={() => onClose}
      styleRoot={{
        padding: 0,
      }}
      styleHeaderModal={{
        paddingLeft: 16,
        paddingRight: 16,
      }}
      bodyChildren={
        <div className="flex flex-col">
          {users.length ? (
            <>
              <div className="grid grid-cols-1 gap-2 mb-[24px]">
                <div className="px-4">
                  <FormInputCard title="Members" fontWeightText="font-semibold">
                    <SelectMulti
                      options={users.map((user) => {
                        return {
                          label: `${user.fullname} - ${user.email}`,
                          value: user.id,
                          isSelected: Object.keys(selectedMembers).includes(
                            user.id
                          ),
                        }
                      })}
                      selectedOption={null}
                      handleChange={(newValue) => {
                        const newSelect = cloneDeep(selectedMembers)
                        if (newSelect[newValue.value]) {
                          delete newSelect[newValue.value]
                        } else {
                          newSelect[newValue.value] = {
                            ...newValue,
                            role: roleGroupMemberId,
                          }
                        }
                        setSelectedMembers(newSelect)
                      }}
                      noOptionsMessage
                      isMulti={false}
                      closeMenuOnSelect={false}
                    />
                  </FormInputCard>
                </div>

                {Object.keys(selectedMembers).length > 0 && (
                  <div className="flex flex-col gap-2">
                    <p className="font-medium text-[14px] leading-[22px] text-[#7A7A7A] px-4">
                      Selected members ({Object.keys(selectedMembers).length})
                    </p>
                    <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto px-4">
                      {Object.keys(selectedMembers).map((key) => {
                        const selected = selectedMembers[key]
                        return (
                          <div
                            key={selected.value}
                            className="flex items-center  p-2 rounded"
                            style={{
                              background: "#F7F7F7",
                              border: "1px solid #E4E4E4",
                            }}
                          >
                            <div className="flex-1">
                              <span
                                className="font-normal"
                                style={{
                                  fontSize: 13,
                                  lineHeight: "22px",
                                  color: "#111111",
                                }}
                              >
                                {selected.label}
                              </span>
                            </div>

                            <div className="flex items-center gap-[8px]">
                              <SelectTippy
                                optionsSelect={roles}
                                activeSelect={selected.role}
                                handleChange={(value) => {
                                  const newSelect = cloneDeep(selectedMembers)
                                  newSelect[key].role = value.id
                                  setSelectedMembers(newSelect)
                                }}
                                fieldSelect="name"
                                fieldActive="id"
                              />
                            </div>
                            <div
                              onClick={() => handleRemoveUser(selected.value)}
                              className="cursor-pointer flex items-center justify-center ml-[16px] bg-[#EA454526] rounded-[4px]"
                              style={{
                                width: 26,
                                height: 26,
                              }}
                            >
                              <IconCloseBlack className="icon-close-red" />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-4 pb-4">
                <ActionFooterModalCard
                  onCloseModal={onClose}
                  onSubmit={onSubmit}
                  handleDisableButton={!Object.keys(selectedMembers).length}
                  titleButton={"Assign"}
                />
              </div>
            </>
          ) : (
            <div className="px-4 pb-4">
              <NoDataCard label="No members found" />
            </div>
          )}
        </div>
      }
    />
  )
}
